// REACT IMPORTS
import React, { useEffect, useState } from "react";
// COMPONENT IMPORTS
import Button from "components/form/Button";
import { valuesProps } from "types/paymentMethods";
import expiryDateValidation, {
  checkAddress,
  checkDefaultMethodExist,
  checkZip,
  decode,
  formatExpirationDate,
  getCardType,
  validateCard,
} from "utils/HelperFunctions";
import {
  addCard,
  cardDetailsGet,
  useBankData,
  useCardData,
  useOldWalletData,
} from "store/slices/paymentMethodSlice";
import SuccessScreen from "components/ui-component/SuccessScreen";

// ICONS IMPORTS
import { CloseIcon } from "icons/payments/CloseIcon";
import { UserAddIcon } from "icons/payments/UserAddIcon";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { updateUserData, useUserData } from "store/slices/authSlice";
import {
  leadData,
  useLeadData,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";
import { UserTickIcon } from "icons/payments/UserTickIcon";
import CardFormComponent from "./CardFormComponent";
import { initialValues } from "utils/countryAddressList";
import { useIsLogin } from "store/slices/commonSlice";
import { countryAndState } from "store/slices/documentsSlice";
import { CardData, CountryState, SelectedState } from "types/paymentMethods";
import { PaymentGateway } from "types/payments";
import { RootState } from "store/store";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useParams } from "react-router-dom";
import AddCardCheckoutForm from "./AddCardCheckoutForm";
import AddCardStaxForm from "./AddCardStaxForm";
import { useTid } from "utils/testid";
import {
  isStripeKeyMissing,
  resolveStripePublishableKey,
  STRIPE_CARD_UNAVAILABLE_MESSAGE,
} from "utils/stripePublishableKey";

// ------------------- Types -------------------
type SetState<T> = React.Dispatch<React.SetStateAction<T>>;

interface AddEditCardFormProps {
  data?: string;
  setData?: SetState<string | null>;
  show: boolean;
  setShow: (show: boolean) => void;
  onCloseClick: () => void;
  setIsEdit?: (isEdit: boolean) => void;
  isEdit?: boolean;
  isAddNewCard?: boolean;
}

type InputChange =
  | React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  | { value?: string; name?: string }
  | { target?: { value?: any } }
  | any;

// ---------------------------------------------

/* ------------------------------------- ADD EDIT CARD ------------------------------------- */

const AddEditCardForm = (props: AddEditCardFormProps) => {
  const tid = useTid();
  const {
    data,
    setData,
    show,
    setShow,
    onCloseClick,
    isEdit,
    setIsEdit,
    isAddNewCard,
  } = props;
  // Dynamically load Stripe when key is available
  const [stripePromise, setStripePromise] = useState<any>(null);
  const { paymentPermission } = useAppSelector(
    (state: RootState) => state.payment
  );
  const stripeKey = resolveStripePublishableKey(paymentPermission, "CC");
  useEffect(() => {
    if (stripeKey) {
      setStripePromise(loadStripe(stripeKey));
    } else {
      setStripePromise(null);
    }
  }, [stripeKey]);
  const dispatch = useAppDispatch();
  const userData = useUserData();
  const { clusterId: cluster_id, id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const accountLeadData = useLeadData();
  const isLogin = useIsLogin();
  const [isErr, setIsErr] = useState(-1);
  const [cardErrMsg, setCardErrMsg] = useState("");
  const portalSettings = usePortalSettingsData();
  const cardList = useCardData();
  const bankList = useBankData();
  const oldWalletData = useOldWalletData();
  const [countriesWithStates, setCountriesWithStates] = useState<
    CountryState[]
  >([]);

  const onGetLeadData = async () => {
    let lead_payload: any = {
      accounts_id: userData?.data?.accountData.id,
      cluster_id: userData?.data?.accountData.clusterid,
      accountid: userData?.data?.accountData.accountid,
    };
    try {
      await dispatch(leadData(lead_payload)).unwrap();
    } catch (error: any) {
      console.log("onGetLeadData error", error);
    }
  };
  useEffect(() => {
    if (
      isLogin &&
      accountLeadData &&
      accountLeadData &&
      Object.keys(accountLeadData).length === 0
    ) {
      onGetLeadData();
    }
  }, [isLogin]);

  const [values, setValues] = useState<valuesProps>(initialValues);
  const [cardNumber, setcardNumber] = useState<string>("");
  const [iconType, setIconType] = useState<string>("");
  const [selectedState, setSelectedState] = useState<SelectedState | undefined>(
    undefined
  );
  const [isDateErr, setIsDateErr] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [successScreen, setSuccessScreen] = useState<boolean>(false);
  const [initialCardValues, setInitialCardValues] = useState(initialValues);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isScreenLoading, setIsScreenLoading] = useState(isEdit);
  const [cardData, setCardData] = useState<CardData | null>(null);

  useEffect(() => {
    if (!show) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const onClose = () => {
    setShow(false);
    setIsEdit && setIsEdit(false);
    setData && setData(null);
    setValues(initialValues);
    setcardNumber("");
    setSelectedState(undefined);
    setIsDateErr(false);
    setSuccessScreen(false);
    setCardData(null);
  };

  useEffect(() => {
    let addressField = checkAddress(
      portalSettings?.cc_billing_address,
      portalSettings?.cc_billing_addr_mandatory,
      values
    );
    let validZip = checkZip(values);

    if (!isEdit) {
      if (
        values?.x_card_num === "" ||
        values?.x_exp_date === "" ||
        values?.x_card_code === "" ||
        values?.choldername === "" ||
        isErr !== -1 ||
        !addressField ||
        !validZip
      ) {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
    } else {
      if (
        values?.x_city === initialCardValues?.x_city &&
        values?.x_address === initialCardValues?.x_address &&
        values?.address2 === initialCardValues?.address2 &&
        values?.x_zip === initialCardValues?.x_zip &&
        values?.x_country === initialCardValues?.x_country &&
        values?.x_state === initialCardValues?.x_state &&
        values?.choldername === initialCardValues?.choldername
      ) {
        setIsDisabled(true);
      } else {
        if (
          values?.choldername === "" ||
          isErr !== -1 ||
          !addressField ||
          !validZip
        ) {
          setIsDisabled(true);
        } else {
          setIsDisabled(false);
        }
      }
    }
  }, [values, isEdit, isErr, initialCardValues, portalSettings]);

  useEffect(() => {
    const getCountryAndState = async () => {
      try {
        if (show) {
          const res: any = await dispatch(countryAndState()).unwrap();
          const data = res?.body?.data || [];
          setCountriesWithStates(data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    getCountryAndState();
  }, [show]);

  const getCardDetails = async () => {
    setIsScreenLoading(true);
    try {
      let payload = {
        slug: data,
      };
      let res: any = await dispatch(cardDetailsGet(payload)).unwrap();
      if (res?.body?.success) {
        const { data } = res?.body;
        setCardData(data);
        let xdata = {
          id: data?.id,
          x_card_num: data?.cc_4digit || "",
          x_exp_date: data?.cc_expiry,
          // x_card_code: data?.cc_cvv || "",
          card_type: data?.cc_type || "",
          choldername: data?.account_name || "",
          x_country: data?.billing_country || "",
          x_address: data?.billing_address || "",
          address2: data?.billing_address2 || "",
          x_city: data?.billing_city || "",
          x_state: data?.billing_state || "",
          x_zip: data?.billing_zip || "",
        };

        setInitialCardValues({
          ...initialCardValues,
          ...xdata,
        });
        setValues({
          ...values,
          ...xdata,
        });
        dispatch(
          updateUserData({
            ...userData?.data?.accountData,
            ...data?.accountData,
          })
        );
        setIconType(data?.cc_type);
        setcardNumber(`XXXX XXXX XXXX ${data?.cc_4digit?.slice(-4)}`);
      }
    } catch (error) {
      console.log("getCardDetails error =====", error);
    } finally {
      setIsScreenLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit && data?.trim() !== "") {
      getCardDetails();
    }
  }, [data, isEdit]);

  useEffect(() => {
    if (
      isEdit &&
      countriesWithStates?.length > 0 &&
      cardData?.billing_country &&
      cardData?.billing_state
    ) {
      const country = countriesWithStates.find(
        (c: any) => c?.country_code === cardData?.billing_country
      );
      if (country && country?.states) {
        const selectedStateObject: any = country.states.find(
          (state: any) =>
            state.state_name === cardData?.billing_state ||
            state.state_code === cardData?.billing_state
        );
        if (selectedStateObject) {
          setSelectedState({
            name: selectedStateObject.state_name,
            value: selectedStateObject.state_name,
          });
          setValues((prev) => ({
            ...prev,
            x_state: selectedStateObject.state_name,
          }));
          setInitialCardValues((prev) => ({
            ...prev,
            x_state: selectedStateObject.state_name,
          }));
        }
      }
    }
  }, [countriesWithStates, cardData, isEdit]);

  const handleChange = (e: InputChange, name: string) => {
    const ev: any = e;
    if (name === "x_card_num") {
      const raw = ev.target?.value ?? ev.value ?? "";
      let icon = getCardType(raw) || iconType;
      if (icon === "amex") {
        if (raw.length < 18) {
          const cardNumber = raw.replace(/\s/g, "");
          const formattedCardNumber = cardNumber
            .replace(/\D/g, "")
            .replace(/(\d{4})(\d{6})(\d{5})/, "$1 $2 $3");
          setcardNumber(formattedCardNumber);
          setValues({ ...values, [name]: raw });
        }
      } else {
        if (raw.length < 20) {
          const cardNumber = raw.replace(/\s/g, "");
          const formattedCardNumber = cardNumber
            .replace(/\D/g, "")
            .replace(/(\d{4})(?=\d)/g, "$1 ");
          setcardNumber(formattedCardNumber);
          setValues({ ...values, [name]: raw });
        }
      }
    } else if (name === "x_card_code") {
      if (iconType === "amex") {
        if (ev.target.value.length < 5) {
          setValues({ ...values, [name]: ev.target.value });
        }
      } else {
        if (ev.target.value.length < 4) {
          setValues({ ...values, [name]: ev.target.value });
        }
      }
    } else if (name === "x_exp_date") {
      const inputValue = ev.target?.value ?? ev.value ?? "";
      setIsDateErr(false);
      if (inputValue?.length < 6) {
        const isValidDate: any = expiryDateValidation(inputValue);
        const formattedDate = formatExpirationDate(inputValue);
        setValues({ ...values, [name]: formattedDate });
        !isValidDate?.isValid && inputValue?.length === 5 && setIsDateErr(true);
      }
    } else if (name === "x_country") {
      setValues({ ...values, [name]: ev?.value });
    } else if (name === "x_state") {
      const stateVal =
        typeof ev === "string"
          ? ev
          : (ev?.name ?? ev?.target?.value ?? ev?.value ?? ev);
      setValues({ ...values, [name]: stateVal });
    } else {
      setValues({ ...values, [name]: ev?.target?.value ?? ev?.value });
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let payload: valuesProps = {
      ...values,
      x_card_num: values?.x_card_num
        ? values?.x_card_num?.replaceAll(" ", "")
        : "",
      cluster_id: userData?.data?.accountData.clusterid,
      accountid: userData?.data?.accountData.accountid,
      accounts_id: userData?.data?.accountData.id,
      x_first_name: userData?.data?.accountData?.first_name,
      x_last_name: userData?.data?.accountData?.last_name,
      x_phone: userData?.data?.accountData?.phone,
      x_email: userData?.data?.accountData?.email,
      lead_id: cardData?.lead_id,
      gateway_id: cardData?.gateway_id,
      gateway_name: cardData?.gateway_name,
      account_holder_name: values?.choldername,
      card_type: values?.x_card_num ? getCardType(values?.x_card_num) : "",
      default: "Y",
      default_profile: "N",
      // default: checkDefaultMethodExist(cardList, bankList, oldWalletData),
      // default_profile: checkDefaultMethodExist(
      //   cardList,
      //   bankList,
      //   oldWalletData
      // ),
    };
    if (isAddNewCard) {
      await onCreateCard(payload);
      return;
    }

    if (isEdit) {
      await onEditCard(payload);
    } else {
      await onCreateCard(payload);
    }
  };

  const onCreateCard = async (val: valuesProps): Promise<void> => {
    setIsLoading(true);
    try {
      let payload: valuesProps = { ...val };
      const res: any = await dispatch(addCard(payload)).unwrap();
      if (res && res?.body?.success) {
        setSuccessScreen(true);
      } else {
        setIsErr(1);
        setCardErrMsg("Your card's number is invalid");
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.log("create card err: -->", error?.message);
    }
  };

  const onEditCard = async (val: valuesProps): Promise<void> => {
    setIsLoading(true);
    try {
      let payload: valuesProps = { ...val };
      payload["isUpdate"] = true;
      payload["client_id"] = cardData?.id;
      const res: any = await dispatch(addCard(payload)).unwrap();
      if (res && res?.body?.success) {
        setSuccessScreen(true);
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.log("create card err: -->", error?.message);
    }
  };

  useEffect(() => {
    if (cardNumber?.includes("x")) {
      return;
    }
    if (!cardNumber) {
      setIconType("");
      setValues({ ...values, card_type: "" });
      return;
    } else {
      setIconType(getCardType(cardNumber));
      setValues({ ...values, card_type: getCardType(cardNumber) });
    }
  }, [cardNumber]);

  if (successScreen) {
    return (
      <div data-testid={tid("card-form-success")}>
        <SuccessScreen
          closeLabel="Close card confirmation"
          title={isEdit ? "Payment Method Updated" : "Payment Method Added"}
          content={
            isEdit ? (
              <>
                Your payment method details have been successfully updated.{" "}
                <br />
                You’re all set for your next transactions.
              </>
            ) : (
              <>
                Thank you for adding a new payment method to your account. You
                can now use it to make payments.
              </>
            )
          }
          onCloseClick={() => {
            onClose();
            onCloseClick();
          }}
        />
      </div>
    );
  }
  if (isScreenLoading) {
    return (
      <div
        className="h-full w-full flex justify-center items-center"
        data-testid={tid("card-form-loading")}
        aria-label="Loading card details"
      >
        <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
      </div>
    );
  }

  return (
    <div
      className="bg-bgPrimary h-full overflow-y-auto lg:pt-[70px] pt-[56px]"
      data-testid={tid(isEdit ? "edit-card-form" : "add-card-form")}
      aria-label={isEdit ? "Update card form" : "Add card form"}
    >
      <div
        className="lg:px-10 px-5 lg:py-5 py-3 bg-bgPrimary flex items-center justify-between fixed top-0 w-full z-10"
        data-testid={tid("card-form-header")}
      >
        <div className="flex items-center gap-2">
          {isEdit ? (
            <UserTickIcon aria-hidden="true" />
          ) : (
            <UserAddIcon aria-hidden="true" />
          )}
          <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
            {isEdit ? "Update card" : "Add new card"}
          </div>
        </div>
        <div className="flex items-center gap-5">
          <div
            role="button"
            tabIndex={0}
            aria-label="Close card form"
            data-testid={tid("card-form-close-button")}
            onClick={() => {
              onClose();
              // onCloseClick();
            }}
            className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          >
            <CloseIcon aria-hidden="true" />
          </div>
        </div>
      </div>
      {paymentPermission?.cc_gateway_name?.name === PaymentGateway.STAX &&
      show &&
      !isEdit ? (
        <AddCardStaxForm
          values={values}
          handleChange={handleChange}
          isEdit={isEdit}
          iconType={iconType}
          isDateErr={isDateErr}
          selectedState={selectedState}
          setSelectedState={setSelectedState}
          show={show}
          setIconType={setIconType}
          accountid={userData?.data?.accountData.accountid}
          clusterId={clusterId}
          email={userData?.data?.accountData.email}
          leadId={accountLeadData?.lead_id}
          setSuccessScreen={setSuccessScreen}
        />
      ) : !isEdit && isStripeKeyMissing(paymentPermission, "CC") ? (
        <div
          role="alert"
          className="text-xs text-red p-5 lg:px-10"
          data-testid={tid("card-form-stripe-unavailable")}
        >
          {STRIPE_CARD_UNAVAILABLE_MESSAGE}
        </div>
      ) : paymentPermission?.cc_gateway_name?.name === PaymentGateway.STRIPE &&
        show &&
        !isEdit &&
        stripePromise ? (
        <Elements stripe={stripePromise}>
          <AddCardCheckoutForm
            values={values}
            handleChange={handleChange}
            isEdit={isEdit}
            iconType={iconType}
            isDateErr={isDateErr}
            selectedState={selectedState}
            setSelectedState={setSelectedState}
            show={show}
            setIconType={setIconType}
            accountid={userData?.data?.accountData.accountid}
            clusterId={clusterId}
            email={userData?.data?.accountData.email}
            leadId={accountLeadData?.lead_id}
            setSuccessScreen={setSuccessScreen}
          />
        </Elements>
      ) : (
        <form onSubmit={onSubmit} data-testid={tid("card-form-fields")}>
          <div className="lg:px-10 flex flex-col gap-5 items-start p-5">
            <CardFormComponent
              values={values}
              handleChange={handleChange}
              isEdit={isEdit}
              cardNumber={cardNumber}
              iconType={iconType}
              isDateErr={isDateErr}
              selectedState={selectedState}
              setSelectedState={setSelectedState}
              validateCard={() =>
                validateCard({ values, iconType, setIsErr, setCardErrMsg })
              }
              isErr={isErr}
              cardErrMsg={cardErrMsg}
              show={show}
            />
          </div>
          <div className="lg:px-10 px-5">
            <Button
              text={isEdit ? "Update Card" : "Add Card"}
              data-testid={tid("card-form-submit-button")}
              aria-label={isEdit ? "Update card" : "Add card"}
              className="w-full"
              type="submit"
              isLoading={isLoading}
              isDisabled={isDisabled}
            />
          </div>
        </form>
      )}
    </div>
  );
};

export default AddEditCardForm;
